// Line-count audit (2026-05-04): 563 lines.
import { useCallback } from 'react';
import { useEditorStore } from '@/stores/editor/editor-store';
import { styleCloneService } from '@/services/ai/StyleCloneService';
import { breakpointPropertyService } from '@/services/ai/BreakpointPropertyService';
import { quickCommandHandler } from '@/services/ai/commands/handlers/QuickCommandHandler';
import { flexContainerCommandHandler } from '@/services/ai/commands/handlers/FlexContainerCommandHandler';
import { chartCommandParser } from '@/services/ai/charts/ChartCommandParser';
import { chartTypeRegistry } from '@/services/ai/charts/ChartTypeRegistry';
import { flexRelationshipService } from '@/services/ai/FlexRelationshipService';
import { clarificationService } from '@/services/ai/ClarificationService';
import { getBreakpointDisplayName } from '@/types/ai';
import { Component, ComponentType, Position, Project, Size } from '@/types';
import type { ClarificationOption, ClarificationRequest } from '@/types/ai';
import type { ComponentUpdatePayload } from '@/types/component-update';
import type { ComponentData, LayoutData, PropertyMessage } from '@/components/ai/types';
import type { ComponentPropertyChanges } from './agentMessagingTypes';
import type { PropertyChange as FlexPropertyChange } from '@/services/ai/FlexRelationshipService';
import type { PropertyChange } from '@/services/ai/commands/BaseCommandHandler';
import {
  getBlockedAICommandPermissionMessage,
  getMutationCategoriesForChanges,
  type AICommandPermissionGuard,
} from '@/services/ai/agentCommandPermissions';
import {
  copySanitizedOwnEnumerableProperties,
  mergeSanitizedRecords,
  sanitizeRecordValue,
} from '@/utils/safeRecordSanitization';

export interface PropertyFlowResponseData {
  properties?: Record<string, unknown>;
  style?: Record<string, unknown>;
  responsivePosition?: Record<string, unknown>;
  success?: boolean;
  content?: string;
  message?: string;
  followUpSuggestions?: string[];
  propertyChanges?: Record<string, unknown>;
  componentData?: ComponentData;
  layoutData?: LayoutData;
  requiresConfirmation?: boolean;
  isComponentCreation?: boolean;
  isMultiComponentCreation?: boolean;
  components?: ComponentData[];
  suggestions?: string[];
  [key: string]: unknown;
}

interface ExecuteDirectCommandArgs {
  userInput: string;
  assistantMessageId: string;
  targetComponentFromReference: Component | null;
  effectiveComponent: Component | null | undefined;
}

interface PropertyCommandExecutionResult {
  handled: boolean;
  shouldUseBackend: boolean;
  data?: PropertyFlowResponseData;
  skipAssistantPropertyParsing: boolean;
}

const GENERIC_CHART_VARIANT_TO_COMPONENT_TYPE: Partial<Record<string, ComponentType>> = {
  line: ComponentType.LINE_CHART,
  bar: ComponentType.BAR_CHART,
  pie: ComponentType.PIE_CHART,
  area: ComponentType.AREA_CHART,
  scatter: ComponentType.SCATTER_CHART,
  heatmap: ComponentType.HEATMAP_CHART,
  treemap: ComponentType.TREEMAP_CHART,
  gauge: ComponentType.GAUGE_CHART,
};

const COMPONENT_TYPE_TO_GENERIC_CHART_VARIANT: Partial<Record<ComponentType, string>> = {
  [ComponentType.LINE_CHART]: "line",
  [ComponentType.BAR_CHART]: "bar",
  [ComponentType.PIE_CHART]: "pie",
  [ComponentType.AREA_CHART]: "area",
  [ComponentType.SCATTER_CHART]: "scatter",
  [ComponentType.HEATMAP_CHART]: "heatmap",
  [ComponentType.TREEMAP_CHART]: "treemap",
  [ComponentType.GAUGE_CHART]: "gauge",
};

function copySafeOwnEnumerableProperties(
  target: Record<string, unknown>,
  source: unknown
): void {
  copySanitizedOwnEnumerableProperties(target, source);
}

function mergeSafeOwnEnumerableProperties(
  ...sources: unknown[]
): Record<string, unknown> {
  return mergeSanitizedRecords(...sources);
}

function resolveConcreteChartComponent(component: Component | null | undefined): Component | null | undefined {
  if (!component || component.type !== ComponentType.CHART) {
    return component;
  }

  const chartVariant = typeof component.properties?.chartType === "string"
    ? component.properties.chartType.toLowerCase()
    : "";
  const concreteType = GENERIC_CHART_VARIANT_TO_COMPONENT_TYPE[chartVariant];

  if (!concreteType) {
    return component;
  }

  return {
    ...component,
    type: concreteType,
  };
}

export interface UseAgentPropertyCommandExecutionDeps {
  component: Component | null | undefined;
  multipleComponents?: Component[];
  isCanvas?: boolean;
  project: Project | null;
  updateComponent: (id: string, updates: Partial<ComponentUpdatePayload>, options?: { mergeBreakpoints?: boolean }) => void;
  moveComponent: (id: string, position: Position) => void;
  resizeComponent: (id: string, size: Size) => void;
  setMessages: React.Dispatch<React.SetStateAction<PropertyMessage[]>>;
  setInputValue: React.Dispatch<React.SetStateAction<string>>;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  setPendingClarification: (request: ClarificationRequest) => void;
  addToHistory: (entry: { command: string; componentType: string; success: boolean; propertyChanges?: Record<string, unknown>; componentId?: string }) => void;
  persistMessage: (msg: PropertyMessage) => Promise<void>;
  generateMessageId: () => string;
  applyPropertyChanges: (messageId: string, changes: ComponentPropertyChanges) => void;
  commandPermissionGuard?: AICommandPermissionGuard;
}

export interface UseAgentPropertyCommandExecutionReturn {
  executeDirectCommand: (args: ExecuteDirectCommandArgs) => Promise<PropertyCommandExecutionResult>;
}

type LocalPropertyChange = PropertyChange | FlexPropertyChange;

function toPropertyChangeRecord(
  changes: LocalPropertyChange | undefined
): Record<string, unknown> | undefined {
  if (!changes) {
    return undefined;
  }

  return mergeSafeOwnEnumerableProperties(changes);
}

function toHistoryPropertyChanges(
  changes: LocalPropertyChange | undefined
): Record<string, unknown> | undefined {
  return toPropertyChangeRecord(changes);
}

export function useAgentPropertyCommandExecution(
  deps: UseAgentPropertyCommandExecutionDeps
): UseAgentPropertyCommandExecutionReturn {
  const {
    component,
    multipleComponents,
    isCanvas,
    project,
    updateComponent,
    moveComponent,
    resizeComponent,
    setMessages,
    setInputValue,
    setIsLoading,
    setPendingClarification,
    addToHistory,
    persistMessage,
    generateMessageId,
    applyPropertyChanges,
    commandPermissionGuard,
  } = deps;

  const executeDirectCommand = useCallback(async ({
    userInput,
    assistantMessageId,
    targetComponentFromReference,
    effectiveComponent,
  }: ExecuteDirectCommandArgs): Promise<PropertyCommandExecutionResult> => {
    const targetComponent = targetComponentFromReference || effectiveComponent || component || multipleComponents?.[0];

    const blockAssistantMessage = async (
      message: string,
    ): Promise<PropertyCommandExecutionResult> => {
      setMessages((previous) => previous.map((current) =>
        current.id === assistantMessageId
          ? { ...current, content: message, isStreaming: false, applied: false }
          : current
      ));
      await persistMessage({
        id: assistantMessageId,
        content: message,
        role: 'assistant',
        timestamp: new Date(),
        isStreaming: false,
      });
      setIsLoading(false);
      return {
        handled: true,
        shouldUseBackend: false,
        skipAssistantPropertyParsing: true,
      };
    };

    let effectiveChartComponent = targetComponent;
    if (
      effectiveChartComponent &&
      !chartTypeRegistry.isChartType(effectiveChartComponent.type) &&
      effectiveChartComponent.type === ComponentType.FLEX_CONTAINER
    ) {
      const effectiveChartComponentId = effectiveChartComponent.id;
      const { components: allComponents } = useEditorStore.getState();
      const chartChild = allComponents.find((current) =>
        current.parentId === effectiveChartComponentId && chartTypeRegistry.isChartType(current.type)
      );

      if (chartChild) {
        effectiveChartComponent = chartChild;
      } else {
        const childIds = allComponents
          .filter((current) => current.parentId === effectiveChartComponentId)
          .map((current) => current.id);
        const grandchildChart = allComponents.find((current) =>
          current.parentId && childIds.includes(current.parentId) && chartTypeRegistry.isChartType(current.type)
        );
        if (grandchildChart) {
          effectiveChartComponent = grandchildChart;
        }
      }
    }

    const parserChartComponent = resolveConcreteChartComponent(effectiveChartComponent);

    const isChartCommand = Boolean(
      parserChartComponent &&
      chartTypeRegistry.isChartType(parserChartComponent.type) &&
      chartCommandParser.isChartCommand(userInput.trim())
    );

    const cloneInfo = targetComponent ? styleCloneService.parseCloneCommand(userInput.trim()) : null;
    const isCloneCommand = Boolean(cloneInfo);
    const isBreakpointCmd = targetComponent ? quickCommandHandler.isBreakpointCommand(userInput.trim()) : false;
    const isMultiSelectCmd = quickCommandHandler.isMultiSelectCommand(userInput.trim());
    const hasMultipleSelected = Boolean(multipleComponents && multipleComponents.length >= 2);
    const isFlexContainerTarget = Boolean(
      targetComponent &&
      (
        targetComponent.type === ComponentType.FLEX_CONTAINER ||
        targetComponent.type === ComponentType.CONTAINER
      )
    );
    const flexContainerResult = isFlexContainerTarget && targetComponent
      ? await flexContainerCommandHandler.parseCommand(userInput.trim(), targetComponent)
      : null;
    const isFlexContainerCmd = Boolean(
      flexContainerResult?.success &&
      flexContainerResult.command?.changes
    );

    const storeStateForFlex = useEditorStore.getState();
    const allComponentsForFlex = storeStateForFlex.components;
    const componentsToAnalyze = multipleComponents && multipleComponents.length > 0
      ? multipleComponents
      : targetComponent ? [targetComponent] : [];
    const flexRelationship = componentsToAnalyze.length > 0
      ? (
          componentsToAnalyze.length === 1
            ? flexRelationshipService.analyzeFlexRelationship(componentsToAnalyze[0], allComponentsForFlex)
            : flexRelationshipService.analyzeMultipleFlexRelationships(componentsToAnalyze, allComponentsForFlex)
        )
      : null;
    const isFlexChildCmd = Boolean(
      flexRelationship?.isFlexChild &&
      flexRelationshipService.isFlexChildCommand(userInput.trim())
    );

    const shouldUseBackend =
      !isChartCommand &&
      !isCloneCommand &&
      !isBreakpointCmd &&
      !(isMultiSelectCmd && hasMultipleSelected) &&
      !isFlexChildCmd &&
      !isFlexContainerCmd;

    if (shouldUseBackend) {
      return {
        handled: false,
        shouldUseBackend: true,
        skipAssistantPropertyParsing: isCloneCommand || isMultiSelectCmd,
      };
    }

    let data: PropertyFlowResponseData = {};

    if (isCloneCommand && targetComponent && cloneInfo) {
      const cloneResult = await styleCloneService.cloneStyles({
        sourceRef: cloneInfo.sourceRef,
        targetComponent,
        propertyScope: cloneInfo.scope,
        context: {
          selectedComponents: multipleComponents || (component ? [component] : []),
          pageId: project?.currentPageId,
        },
      });

      if (cloneResult.success && cloneResult.clonedStyles) {
        const description = styleCloneService.getCloneDescription(cloneResult, cloneInfo.scope);
        data = {
          success: true,
          content: cloneResult.warnings.length > 0
            ? `✅ ${description}\n⚠️ ${cloneResult.warnings.join(', ')}`
            : `✅ ${description}`,
          propertyChanges: {
            ...mergeSafeOwnEnumerableProperties(cloneResult.clonedStyles),
            targetComponentId: targetComponent.id,
          },
        };
      } else if (
        cloneResult.needsClarification &&
        cloneResult.matchedComponents &&
        cloneResult.matchedComponents.length > 0
      ) {
        const clarificationOptions: ClarificationOption[] = cloneResult.matchedComponents
          .slice(0, 4)
          .map((match, index) => ({
            id: match.id,
            label: match.name || match.type,
            description: `${match.type} - ${match.matchReason}`,
            isRecommended: index === 0,
            metadata: { componentId: match.id, confidence: match.confidence },
          }));

        const clarificationRequest: ClarificationRequest = {
          id: `clone-clarify-${Date.now()}`,
          type: 'component_selection',
          question: `Which component would you like to copy the ${cloneInfo.scope === 'all' ? 'style' : cloneInfo.scope} from?`,
          options: clarificationOptions,
          allowCustomInput: false,
          context: {
            originalCommand: userInput.trim(),
            detectedIntent: 'style_clone',
            targetComponentId: targetComponent.id,
            targetComponentType: targetComponent.type,
            matchedComponents: cloneResult.matchedComponents.map((match) => ({
              id: match.id,
              name: match.name,
              type: match.type,
              confidence: match.confidence,
              matchReason: match.matchReason,
            })),
            pendingChanges: undefined,
            currentValues: { cloneScope: cloneInfo.scope },
          },
          createdAt: new Date(),
        };

        clarificationService.registerClarification(clarificationRequest);
        setPendingClarification(clarificationRequest);

        const clarificationUserMessage: PropertyMessage = {
          id: generateMessageId(),
          content: userInput.trim(),
          role: 'user',
          timestamp: new Date(),
        };
        setMessages((previous) => [...previous, clarificationUserMessage]);
        await persistMessage(clarificationUserMessage);

        const matchList = cloneResult.matchedComponents
          .slice(0, 4)
          .map((match, index) => `${index + 1}. "${match.name}" (${match.type}) - ${match.matchReason}`)
          .join('\n');
        const clarificationMessage: PropertyMessage = {
          id: generateMessageId(),
          content: `${clarificationRequest.question}\n\n${matchList}`,
          role: 'assistant',
          timestamp: new Date(),
        };
        setMessages((previous) => [...previous, clarificationMessage]);
        await persistMessage(clarificationMessage);
        setInputValue('');
        setIsLoading(false);
        return {
          handled: true,
          shouldUseBackend: false,
          skipAssistantPropertyParsing: true,
        };
      } else {
        data = {
          success: false,
          content: cloneResult.error || `Could not clone styles from "${cloneInfo.sourceRef}"`,
          suggestions: styleCloneService.getSuggestions(useEditorStore.getState().components),
        };
      }
    } else if (isBreakpointCmd && targetComponent) {
      const breakpointResult = quickCommandHandler.parseBreakpointCommand(userInput.trim(), targetComponent);

      if (breakpointResult) {
        const targetBreakpoint = breakpointResult.breakpoint || 'desktop';
        const applicationResult = breakpointResult.responsiveStyles
          ? breakpointPropertyService.applyResponsiveStyles(targetComponent, breakpointResult.responsiveStyles)
          : breakpointPropertyService.applyToBreakpoint(targetComponent, breakpointResult, targetBreakpoint);

        if (applicationResult.success && applicationResult.componentUpdate) {
          const permissionResult = commandPermissionGuard?.({
            categories: getMutationCategoriesForChanges(applicationResult.componentUpdate),
            project,
            targetComponents: [targetComponent],
          });

          if (permissionResult && !permissionResult.allowed) {
            return blockAssistantMessage(
              getBlockedAICommandPermissionMessage(permissionResult, 'layout')
            );
          }

          updateComponent(targetComponent.id, applicationResult.componentUpdate);
          const affectedBreakpointNames = applicationResult.affectedBreakpoints
            .map((breakpoint) => getBreakpointDisplayName(breakpoint))
            .join(', ');
          const finalMessage = applicationResult.warnings.length > 0
            ? `✅ ${applicationResult.description || `Applied changes to ${affectedBreakpointNames}`}\n⚠️ ${applicationResult.warnings.join(', ')}`
            : `✅ ${applicationResult.description || `Applied changes to ${affectedBreakpointNames}`}`;

          setMessages((previous) => previous.map((message) =>
            message.id === assistantMessageId
              ? { ...message, content: finalMessage, isStreaming: false, applied: true }
              : message
          ));
          await persistMessage({
            id: assistantMessageId,
            content: finalMessage,
            role: 'assistant',
            timestamp: new Date(),
            isStreaming: false,
          });

          addToHistory({
            command: userInput,
            componentType: targetComponent.type,
            success: true,
            propertyChanges: applicationResult.componentUpdate,
            componentId: targetComponent.id,
          });
          setIsLoading(false);
          return {
            handled: true,
            shouldUseBackend: false,
            skipAssistantPropertyParsing: false,
          };
        }

        data = {
          success: false,
          content: applicationResult.description || 'Could not apply breakpoint changes',
          suggestions: breakpointPropertyService.getBreakpointExamples(),
        };
      } else {
        data = {
          success: false,
          content: 'Could not parse breakpoint command',
          suggestions: breakpointPropertyService.getBreakpointExamples(),
        };
      }
    } else if (isFlexChildCmd && flexRelationship && flexRelationship.isFlexChild) {
      // Intentional local exception: flex child commands can mutate the parent
      // container and sibling children, which CommandParser cannot infer alone.
      const translation = flexRelationshipService.translateCommand(userInput.trim(), flexRelationship);

      if (translation) {
        const flexTargets = [
          flexRelationship.parentId
            ? allComponentsForFlex.find((current) => current.id === flexRelationship.parentId)
            : undefined,
          ...(flexRelationship.siblingIds || [])
            .map((id) => allComponentsForFlex.find((current) => current.id === id)),
        ].filter((current): current is Component => current !== undefined);
        const flexCategories = [
          ...getMutationCategoriesForChanges(toPropertyChangeRecord(translation.parentChanges)),
          ...(translation.childChanges || []).flatMap((childChange) =>
            getMutationCategoriesForChanges(toPropertyChangeRecord(childChange))
          ),
        ];
        const permissionResult = commandPermissionGuard?.({
          categories: flexCategories.length > 0 ? flexCategories : ['layout'],
          project,
          targetComponents: flexTargets,
        });

        if (permissionResult && !permissionResult.allowed) {
          return blockAssistantMessage(
            getBlockedAICommandPermissionMessage(permissionResult, 'layout')
          );
        }

        if (translation.parentChanges && flexRelationship.parentId) {
          const parentComponent = allComponentsForFlex.find((current) => current.id === flexRelationship.parentId);
          if (translation.parentChanges.style && parentComponent) {
            updateComponent(flexRelationship.parentId, {
              style: mergeSafeOwnEnumerableProperties(parentComponent.style, translation.parentChanges.style),
            });
          }
          if (translation.parentChanges.properties && parentComponent) {
            updateComponent(flexRelationship.parentId, {
              properties: mergeSafeOwnEnumerableProperties(parentComponent.properties, translation.parentChanges.properties),
            });
          }
        }

        if (translation.childChanges && translation.childChanges.length > 0) {
          const siblingIds = flexRelationship.siblingIds || [];
          for (let index = 0; index < translation.childChanges.length && index < siblingIds.length; index += 1) {
            const childChange = translation.childChanges[index];
            const childId = siblingIds[index];
            if (!childId || !childChange) {
              continue;
            }

            const childComponent = allComponentsForFlex.find((current) => current.id === childId);
            if (!childComponent) {
              continue;
            }

            if (childChange.style) {
              updateComponent(childId, {
                style: mergeSafeOwnEnumerableProperties(childComponent.style, childChange.style),
              });
            }
            if (childChange.size) {
              const currentSize = childComponent.size || {};
              resizeComponent(childId, {
                width: childChange.size.width !== undefined
                  ? (typeof childChange.size.width === 'number' ? childChange.size.width : parseFloat(String(childChange.size.width)) || currentSize.width || 100)
                  : currentSize.width || 100,
                height: childChange.size.height !== undefined
                  ? (typeof childChange.size.height === 'number' ? childChange.size.height : parseFloat(String(childChange.size.height)) || currentSize.height || 100)
                  : currentSize.height || 100,
              });
            }
          }
        }

        setMessages((previous) => previous.map((message) =>
          message.id === assistantMessageId
            ? { ...message, content: translation.description, isStreaming: false, applied: true }
            : message
        ));
        await persistMessage({
          id: assistantMessageId,
          content: translation.description,
          role: 'assistant',
          timestamp: new Date(),
          isStreaming: false,
        });

        addToHistory({
          command: userInput,
          componentType: targetComponent?.type || 'flex-container',
          success: true,
          propertyChanges: toHistoryPropertyChanges(translation.parentChanges) || {},
          componentId: flexRelationship.parentId ?? undefined,
        });
        setIsLoading(false);
        return {
          handled: true,
          shouldUseBackend: false,
          skipAssistantPropertyParsing: false,
        };
      }

      data = {
        success: false,
        content: `⚠️ Could not translate this command for flex children.\n\nTry commands like:\n• "align left/center/right"\n• "distribute evenly"\n• "gap 16" or "add 20px gap"\n• "equal width"`,
        suggestions: flexRelationshipService.getFlexSuggestions(flexRelationship),
      };
    } else if (isMultiSelectCmd && hasMultipleSelected && multipleComponents) {
      const multiStoreState = useEditorStore.getState();
      const freshMultiComponents = multipleComponents.map((current) =>
        multiStoreState.components.find((storeComponent) => storeComponent.id === current.id) || current
      );
      const multiSelectResult = quickCommandHandler.executeMultiSelectOperation(
        userInput.trim(),
        freshMultiComponents,
        multiStoreState.components
      );

      if (multiSelectResult.success) {
        const multiPermission = commandPermissionGuard?.({
          categories: [
            ...(multiSelectResult.positionChanges?.length ? ['layout' as const] : []),
            ...(multiSelectResult.sizeChanges?.length ? ['layout' as const] : []),
          ],
          project,
          targetComponents: freshMultiComponents,
        });

        if (multiPermission && !multiPermission.allowed) {
          return blockAssistantMessage(
            getBlockedAICommandPermissionMessage(multiPermission, 'layout')
          );
        }

        if (multiSelectResult.positionChanges && multiSelectResult.positionChanges.length > 0) {
          for (const change of multiSelectResult.positionChanges) {
            const componentToMove = freshMultiComponents.find((current) => current.id === change.id);
            if (componentToMove) {
              moveComponent(change.id, {
                x: change.x !== undefined ? change.x : componentToMove.position?.x || 0,
                y: change.y !== undefined ? change.y : componentToMove.position?.y || 0,
                z: componentToMove.position?.z || 1,
              });
            }
          }
        }

        if (multiSelectResult.sizeChanges && multiSelectResult.sizeChanges.length > 0) {
          for (const change of multiSelectResult.sizeChanges) {
            const componentToResize = freshMultiComponents.find((current) => current.id === change.id);
            if (componentToResize) {
              const widthValue = change.width !== undefined ? change.width : componentToResize.size?.width;
              const heightValue = change.height !== undefined ? change.height : componentToResize.size?.height;
              resizeComponent(change.id, {
                width: typeof widthValue === 'number' ? widthValue : parseFloat(String(widthValue)) || 100,
                height: typeof heightValue === 'number' ? heightValue : parseFloat(String(heightValue)) || 100,
              });
            }
          }
        }

        const successMessage = `✅ ${multiSelectResult.description}`;
        setMessages((previous) => previous.map((message) =>
          message.id === assistantMessageId
            ? { ...message, content: successMessage, isStreaming: false, applied: true }
            : message
        ));
        await persistMessage({
          id: assistantMessageId,
          content: successMessage,
          role: 'assistant',
          timestamp: new Date(),
          isStreaming: false,
        });

        addToHistory({
          command: userInput,
          componentType: multipleComponents[0]?.type || 'unknown',
          success: true,
          propertyChanges: {
            positionChanges: multiSelectResult.positionChanges,
            sizeChanges: multiSelectResult.sizeChanges,
          },
          componentId: multipleComponents[0]?.id,
        });
        setIsLoading(false);
        return {
          handled: true,
          shouldUseBackend: false,
          skipAssistantPropertyParsing: true,
        };
      }

      data = {
        success: false,
        content: multiSelectResult.description.includes('at least')
          ? `⚠️ ${multiSelectResult.description}\n\nSelect multiple components by:\n• Shift+click to add to selection\n• Cmd/Ctrl+click to toggle selection\n• Drag to marquee select`
          : `❌ ${multiSelectResult.description}`,
        suggestions: quickCommandHandler.getMultiSelectSuggestions(),
      };
    } else if (isMultiSelectCmd && !hasMultipleSelected) {
      data = {
        success: false,
        content: `⚠️ This command requires multiple components to be selected.\n\nCurrently selected: ${multipleComponents?.length || 0}\n\nSelect multiple components by:\n• Shift+click to add to selection\n• Cmd/Ctrl+click to toggle selection\n• Drag to marquee select`,
        suggestions: quickCommandHandler.getMultiSelectSuggestions(),
      };
    } else if (isFlexContainerCmd && flexContainerResult?.command?.changes && targetComponent) {
      const propertyChanges = mergeSafeOwnEnumerableProperties(
        flexContainerResult.command.changes,
        { targetComponentId: targetComponent.id }
      );

      data = {
        success: true,
        content: flexContainerResult.command.description || '✅ Flex container updated successfully',
        propertyChanges,
      };
    } else if (parserChartComponent) {
      const result = await chartCommandParser.parseCommand(userInput.trim(), parserChartComponent);

      if (result.success && result.command?.changes) {
        const propertyChanges: Record<string, unknown> = {};
        if (result.command.changes.properties) {
          copySafeOwnEnumerableProperties(propertyChanges, result.command.changes.properties);
        }
        if (result.command.changes.style) {
          copySafeOwnEnumerableProperties(propertyChanges, result.command.changes.style);
        }
        if (result.command.changes.data) {
          propertyChanges.data = sanitizeRecordValue(result.command.changes.data);
        }
        if (result.command.changes.type) {
          if (effectiveChartComponent?.type === ComponentType.CHART) {
            const nextChartVariant =
              COMPONENT_TYPE_TO_GENERIC_CHART_VARIANT[result.command.changes.type];

            if (nextChartVariant) {
              propertyChanges.chartType = nextChartVariant;
            } else {
              propertyChanges.type = result.command.changes.type;
            }
          } else {
            propertyChanges.type = result.command.changes.type;
          }
        }
        if (parserChartComponent.id !== component?.id) {
          propertyChanges.targetComponentId = parserChartComponent.id;
        }

        data = {
          success: true,
          content: result.command.description || '✅ Chart updated successfully',
          propertyChanges,
        };
      } else {
        data = {
          success: false,
          content: result.error || 'Could not understand the chart command',
          suggestions: result.suggestions,
        };
      }
    } else {
      data = {
        success: false,
        content: 'Unable to process command without a component',
      };
    }

    return {
      handled: false,
      shouldUseBackend: false,
      data,
      skipAssistantPropertyParsing: isCloneCommand || isMultiSelectCmd,
    };
  }, [
    addToHistory,
    applyPropertyChanges,
    commandPermissionGuard,
    component,
    generateMessageId,
    isCanvas,
    moveComponent,
    multipleComponents,
    persistMessage,
    project,
    resizeComponent,
    setInputValue,
    setIsLoading,
    setMessages,
    setPendingClarification,
    updateComponent,
  ]);

  return { executeDirectCommand };
}
